import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

export interface PeriodDisplayProps {
	value?: number; // Always in minutes
	className?: string;
	showUnit?: boolean; // Whether to show the unit label (ignored in humanized mode, kept for backward compat)
}

interface DurationPart {
	value: number;
	unitSingular: string;
	unitPlural: string;
}

/**
 * PeriodDisplay - Display-only period component
 *
 * - Reads values in minutes
 * - Humanizes duration into days, hours, and minutes
 * - Examples: "11 Hours", "1 Day 3 Hours 30 Minutes", "45 Minutes"
 * - Formats the display nicely with unit labels
 */
export const PeriodDisplay: React.FC<PeriodDisplayProps> = ({
	value = 0,
	className,
	showUnit = true
}) => {
	const { t } = useTranslation('common');

	const parts = React.useMemo((): DurationPart[] => {
		if (value === 0) {
			return [{ value: 0, unitSingular: 'minute', unitPlural: 'minutes' }];
		}

		const result: DurationPart[] = [];
		let remaining = value;

		const days = Math.floor(remaining / 1440); // 60 * 24
		if (days > 0) {
			result.push({ value: days, unitSingular: 'day', unitPlural: 'days' });
			remaining -= days * 1440;
		}

		const hours = Math.floor(remaining / 60);
		if (hours > 0) {
			result.push({ value: hours, unitSingular: 'hour', unitPlural: 'hours' });
			remaining -= hours * 60;
		}

		if (remaining > 0) {
			result.push({ value: remaining, unitSingular: 'minute', unitPlural: 'minutes' });
		}

		return result;
	}, [value]);

	const formattedParts = React.useMemo(() => {
		return parts.map(part => {
			const unitKey = part.value === 1 ? part.unitSingular : part.unitPlural;
			const label = t(unitKey);
			return { value: part.value, label };
		});
	}, [parts, t]);

	return (
		<span className={cn('text-sm', className)}>
			{formattedParts.map((part, index) => (
				<React.Fragment key={part.label}>
					{index > 0 && ' '}
					{part.value}
					{showUnit && <span className="ml-1 text-muted-foreground">{part.label}</span>}
				</React.Fragment>
			))}
		</span>
	);
};

PeriodDisplay.displayName = 'PeriodDisplay';
